// src/pages/Convocations/hooks/useConvocationsPage.js

import { useCallback, useEffect, useMemo, useState } from "react";

import { toast } from "react-toastify";

import { useUser } from "../../Home/UserContext";

import { useConvocation } from "../../../hooks/ConvocationContext";

import { getRoleFromRoleId } from "../../../utils/roleHelper";

import {
  getAllEventsByActorId,
  respondToConvocation,
} from "../../../services/acteureventServices";

import { getActeursByUser } from "../../../services/userServices";

import { DEFAULT_CONVOCATION_FILTERS } from "../data/convocation.data";

import {
  filterAndSortConvocations,
  getActiveFilterCount,
  getConvocationStats,
  getStoredUser,
  getUniqueSortedValues,
  normalizeConvocationsFromActor,
  normalizeResponseStatus,
} from "../utils/convocation.utils";

export default function useConvocationsPage() {
  const userContext = useUser();

  const user = userContext?.user ?? getStoredUser();

  const { syncPendingConvocations } = useConvocation();

  const userId = user?.userId || user?.id || null;

  const userRole = getRoleFromRoleId(user?.role_id);

  const [actors, setActors] = useState([]);

  const [convocations, setConvocations] = useState([]);

  const [loading, setLoading] = useState(true);

  const [refreshing, setRefreshing] = useState(false);

  const [savingResponse, setSavingResponse] = useState(false);

  const [filters, setFilters] = useState({
    ...DEFAULT_CONVOCATION_FILTERS,
  });

  const [selectedEvent, setSelectedEvent] = useState(null);

  const [textModal, setTextModal] = useState({
    isOpen: false,
    title: "",
    content: "",
    allowHtml: false,
  });

  const [responseModal, setResponseModal] = useState({
    isOpen: false,
    convocation: null,
    mode: "rejected",
  });

  const loadConvocations = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        if (!userId) {
          setActors([]);
          setConvocations([]);

          syncPendingConvocations([]);

          return [];
        }

        const actorsData = await getActeursByUser(userId);

        if (!Array.isArray(actorsData) || actorsData.length === 0) {
          setActors([]);
          setConvocations([]);

          syncPendingConvocations([]);

          return [];
        }

        setActors(actorsData);

        const actorResults = await Promise.allSettled(
          actorsData.map((actor) => getAllEventsByActorId(actor.id)),
        );

        const actorsWithEvents = actorResults
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value)
          .filter(Boolean);

        const failedActorCount = actorResults.filter(
          (result) => result.status === "rejected",
        ).length;

        if (failedActorCount > 0) {
          toast.warn(
            `${failedActorCount} profil${
              failedActorCount > 1 ? "s" : ""
            } acteur n’a pas pu être chargé.`,
          );
        }

        const nextConvocations = actorsWithEvents.flatMap((actorData) =>
          normalizeConvocationsFromActor(actorData),
        );

        setConvocations(nextConvocations);

        /*
         * Synchronise immédiatement
         * le compteur de la Navbar.
         */
        syncPendingConvocations(nextConvocations);

        return nextConvocations;
      } catch (error) {
        console.error("Erreur lors du chargement des convocations :", error);

        setActors([]);
        setConvocations([]);

        syncPendingConvocations([]);

        toast.error(
          error?.response?.data?.message ||
            "Impossible de charger vos convocations.",
        );

        return [];
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [syncPendingConvocations, userId],
  );

  useEffect(() => {
    loadConvocations();
  }, [loadConvocations]);

  const stats = useMemo(
    () => getConvocationStats(convocations),
    [convocations],
  );

  const typeOptions = useMemo(
    () =>
      getUniqueSortedValues(
        convocations,
        (convocation) => convocation?.Event?.type_event,
      ),
    [convocations],
  );

  const posteOptions = useMemo(
    () =>
      getUniqueSortedValues(convocations, (convocation) => convocation?.poste),
    [convocations],
  );

  const filteredConvocations = useMemo(
    () => filterAndSortConvocations(convocations, filters),
    [convocations, filters],
  );

  const activeFilterCount = useMemo(
    () => getActiveFilterCount(filters),
    [filters],
  );

  const setFilter = useCallback((name, value) => {
    setFilters((previousFilters) => ({
      ...previousFilters,
      [name]: value,
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      ...DEFAULT_CONVOCATION_FILTERS,
    });
  }, []);

  const filterByStatus = useCallback((status) => {
    setFilters((previousFilters) => {
      const nextStatus =
        previousFilters.selectedStatus === status ? "" : status;

      return {
        ...previousFilters,
        selectedStatus: nextStatus,

        showOnlyFuture:
          nextStatus === "pending" ? true : previousFilters.showOnlyFuture,
      };
    });
  }, []);

  const showPendingConvocations = useCallback(() => {
    setFilters((previousFilters) => ({
      ...previousFilters,
      selectedStatus: "pending",
      showOnlyFuture: true,
      sortBy: "date-asc",
    }));
  }, []);

  const openEventModal = useCallback((event) => {
    setSelectedEvent(event || null);
  }, []);

  const closeEventModal = useCallback(() => {
    setSelectedEvent(null);
  }, []);

  const openTextModal = useCallback(({ title, content, allowHtml = false }) => {
    setTextModal({
      isOpen: true,
      title: title || "Informations",
      content: content || "",
      allowHtml,
    });
  }, []);

  const closeTextModal = useCallback(() => {
    setTextModal({
      isOpen: false,
      title: "",
      content: "",
      allowHtml: false,
    });
  }, []);

  const openResponseModal = useCallback((convocation, mode) => {
    setResponseModal({
      isOpen: true,
      convocation,
      mode,
    });
  }, []);

  const closeResponseModal = useCallback(() => {
    setResponseModal({
      isOpen: false,
      convocation: null,
      mode: "rejected",
    });
  }, []);

  const handleResponseSubmit = useCallback(
    async (payload) => {
      const selectedConvocation = responseModal.convocation;

      if (!selectedConvocation) {
        return;
      }

      const nextStatus = normalizeResponseStatus(payload.response_status);

      try {
        setSavingResponse(true);

        await respondToConvocation(
          selectedConvocation.acteur_id,
          selectedConvocation.event_id,
          nextStatus,
          payload.response_reason || "",
          {
            need_transport_support: payload.need_transport_support,

            need_accommodation_support: payload.need_accommodation_support,

            support_request_comment: payload.support_request_comment,
          },
        );

        const respondedAt = new Date().toISOString();

        const nextConvocations = convocations.map((convocation) => {
          const isSameConvocation =
            String(convocation.id) === String(selectedConvocation.id);

          if (!isSameConvocation) {
            return convocation;
          }

          return {
            ...convocation,

            response_status: nextStatus,

            response_reason: payload.response_reason || "",

            responded_at: respondedAt,

            need_transport_support: payload.need_transport_support,

            need_accommodation_support: payload.need_accommodation_support,

            support_request_comment: payload.support_request_comment || "",
          };
        });

        /*
         * Mise à jour locale immédiate.
         */
        setConvocations(nextConvocations);

        /*
         * Mise à jour immédiate du
         * compteur dans la Navbar.
         */
        syncPendingConvocations(nextConvocations);

        toast.success(
          nextStatus === "accepted"
            ? "Convocation acceptée."
            : "Convocation refusée.",
        );

        closeResponseModal();

        /*
         * Resynchronisation finale
         * avec les données serveur.
         */
        await loadConvocations({
          silent: true,
        });
      } catch (error) {
        console.error("Erreur lors de la réponse à la convocation :", error);

        toast.error(
          error?.response?.data?.message ||
            "Erreur lors de l’enregistrement de votre réponse.",
        );
      } finally {
        setSavingResponse(false);
      }
    },
    [
      closeResponseModal,
      convocations,
      loadConvocations,
      responseModal.convocation,
      syncPendingConvocations,
    ],
  );

  return {
    user,
    userRole,

    actors,
    convocations,
    filteredConvocations,

    stats,
    typeOptions,
    posteOptions,

    filters,
    activeFilterCount,

    loading,
    refreshing,
    savingResponse,

    selectedEvent,
    textModal,
    responseModal,

    setFilter,
    resetFilters,
    filterByStatus,
    showPendingConvocations,

    loadConvocations,

    openEventModal,
    closeEventModal,

    openTextModal,
    closeTextModal,

    openResponseModal,
    closeResponseModal,

    handleResponseSubmit,
  };
}
